/* ===========================================================
   lib/theme.js — the look: same as Weather Protocol, or your own

   By default Docs Protocol wears the theme you picked in Weather
   Protocol (it reads your synced Weather Protocol settings). In
   Settings you can split them and pick something else here.

   A theme is four colours (accent, background, panel, text); every
   other shade is derived, with faint text pushed until it still reads
   at 4.8:1 against the page. The same maths as Weather Protocol's
   themes.js, so a theme looks like itself on both sites.

   The last applied colours are cached in localStorage so the next
   launch paints instantly (public/boot.js reads them before the app
   loads).
   =========================================================== */

export const PRESETS = [
  { id: 'light', name: 'Light', a: '#2563eb', bg: '#f3f4f6', panel: '#ffffff', fg: '#1f2328' },
  { id: 'dark', name: 'Dark', a: '#8ab4ff', bg: '#16181d', panel: '#1f2228', fg: '#e6e8ec' },
  { id: 'forest', name: 'Forest', a: '#7bd88f', bg: '#06110a', panel: '#0c1a11', fg: '#d5e8d9' },
  { id: 'sunset', name: 'Sunset', a: '#ff8a3d', bg: '#130a0b', panel: '#1c0f11', fg: '#f3ddd2' },
  { id: 'ocean', name: 'Deep Ocean', a: '#2ec4ff', bg: '#03101a', panel: '#071a28', fg: '#cfe6f2' },
  { id: 'midnight', name: 'Midnight', a: '#a78bfa', bg: '#0a0815', panel: '#120e22', fg: '#e2dcf5' },
  { id: 'desert', name: 'Desert', a: '#e0a458', bg: '#14100a', panel: '#1f170e', fg: '#efe2cc' },
  { id: 'synthwave', name: 'Synthwave', a: '#ff4fd8', bg: '#0d0618', panel: '#170b28', fg: '#f1d9f7' },
  { id: 'storm', name: 'Storm Cell', a: '#ffd23f', bg: '#0a0c10', panel: '#12161d', fg: '#dfe3ea' },
  { id: 'arctic', name: 'Arctic', a: '#0a74c4', bg: '#eaf1f6', panel: '#ffffff', fg: '#0f2333' },
  { id: 'paper', name: 'Paper', a: '#b43c0a', bg: '#f3eee3', panel: '#fffdf8', fg: '#2b2118' },
];

const ACCENTS = { green: '#00ff41', amber: '#ffb000', cyan: '#00e5ff', ice: '#b8d4ff', red: '#ff3b30' };
const MOODS = {
  clearDay: { a: '#ffc145', bg: '#0f0c06', panel: '#18130b', fg: '#efe6d2' },
  clearNight: { a: '#8fb8ff', bg: '#04060e', panel: '#0a0e1b', fg: '#d9e2f5' },
};

/* ---------- colour maths (from Weather Protocol) ---------- */

const rgb = hex => { const n = parseInt(hex.slice(1), 16); return [(n >> 16) & 255, (n >> 8) & 255, n & 255]; };
const rgba = (hex, a) => { const [r, g, b] = rgb(hex); return `rgba(${r},${g},${b},${a})`; };
const toHex = ([r, g, b]) => `#${((1 << 24) | (Math.round(r) << 16) | (Math.round(g) << 8) | Math.round(b)).toString(16).slice(1)}`;
export const mixHex = (a, b, t) => { const x = rgb(a), y = rgb(b); return toHex(x.map((v, i) => v + (y[i] - v) * t)); };
export const okHex = v => /^#[0-9a-f]{6}$/i.test(v || '');

function lum(hex) {
  const f = c => { c /= 255; return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4; };
  const [r, g, b] = rgb(hex);
  return 0.2126 * f(r) + 0.7152 * f(g) + 0.0722 * f(b);
}
export function contrast(a, b) {
  const [x, y] = [lum(a), lum(b)].sort((m, n) => n - m);
  return (x + 0.05) / (y + 0.05);
}

/** Four colours → every CSS variable the app uses. */
export function derive({ a, bg, panel, fg }) {
  const light = lum(bg) > 0.35;
  let t = 0.52, fg3 = mixHex(fg, bg, t);
  while (contrast(fg3, panel) < 4.8 && t > 0.08) { t -= 0.04; fg3 = mixHex(fg, bg, t); }
  const fg2 = mixHex(fg, bg, Math.max(0.06, t - 0.2));
  // accent used as text on the page needs to read too
  let aText = a, k = 0;
  while (contrast(aText, panel) < 4.5 && k < 12) { aText = mixHex(aText, light ? '#000000' : '#ffffff', 0.12); k++; }
  const onA = contrast('#ffffff', a) >= contrast('#111111', a) ? '#ffffff' : '#111111';
  return {
    light,
    vars: {
      '--a': a, '--a-text': aText, '--on-a': onA,
      '--a-dim': mixHex(a, panel, 0.3), '--a-deep': mixHex(a, bg, 0.62),
      '--a-wash': rgba(a, light ? 0.12 : 0.16), '--a-line': rgba(a, light ? 0.45 : 0.4),
      '--bg': bg, '--bg-2': mixHex(bg, fg, 0.04),
      '--panel': panel, '--panel-2': mixHex(panel, fg, 0.04), '--raised': mixHex(panel, fg, light ? 0.07 : 0.09),
      '--fg': fg, '--fg-2': fg2, '--fg-3': fg3,
      '--line': light ? 'rgba(0,0,0,.1)' : rgba(fg, 0.1),
      '--line-2': light ? 'rgba(0,0,0,.18)' : rgba(fg, 0.18),
      '--shadow': light ? '0 1px 2px rgba(0,0,0,.06), 0 4px 16px rgba(0,0,0,.06)' : '0 1px 2px rgba(0,0,0,.4), 0 6px 20px rgba(0,0,0,.35)',
      '--ins': light ? '#cdf3d6' : '#16452a', '--ins-fg': light ? '#0b5a24' : '#9ff0b8',
      '--del': light ? '#fbd5d5' : '#5a1f24', '--del-fg': light ? '#8f1d1d' : '#ffb4b4',
      '--warn': light ? '#9a5b00' : '#ffc46b', '--bad': light ? '#b42318' : '#ff8a80', '--good': light ? '#1a7f37' : '#7ee2a0',
    },
  };
}

/* ---------- which four colours ---------- */

/** The theme someone has in Weather Protocol (from their synced settings). */
export function wpColours(wp = {}) {
  const id = wp.pTheme || 'forest';
  const classic = () => ({
    a: ACCENTS[wp.accent] || ACCENTS.green,
    bg: wp.surface === 'light' ? '#eef2ef' : '#05070a',
    panel: wp.surface === 'light' ? '#ffffff' : '#0a0f15',
    fg: wp.surface === 'light' ? '#12201a' : '#d6e3dd',
  });
  if (id === 'classic') return { id, name: 'Classic', ...classic() };
  if (id === 'custom') {
    const c = { ...classic(), ...(wp.pCustom || {}) };
    for (const k of ['a', 'bg', 'panel', 'fg']) if (!okHex(c[k])) c[k] = classic()[k];
    return { id, name: 'Your own colours', ...c };
  }
  if (id === 'dynamic') {
    // Weather Protocol's Dynamic follows the sky. Here: its base theme, or day/night colours.
    const base = wp.pDynBase && wp.pDynBase !== 'weather' ? wpColours({ ...wp, pTheme: wp.pDynBase }) : null;
    const hour = new Date().getHours();
    const mood = hour >= 7 && hour < 19 ? MOODS.clearDay : MOODS.clearNight;
    return { id, name: 'Dynamic', ...(base ? { a: base.a, bg: base.bg, panel: base.panel, fg: base.fg } : mood) };
  }
  const p = PRESETS.find(x => x.id === id) || PRESETS.find(x => x.id === 'forest');
  return { ...p };
}

const systemDark = () => typeof matchMedia !== 'undefined' && matchMedia('(prefers-color-scheme: dark)').matches;

/** profile.themeMode 'wp' → Weather Protocol's look; 'own' → profile.theme here. Signed out: follow the device. */
export function coloursFor(profile, wp) {
  // signed out: Weather Protocol's default look (Forest), like the sign-in page there
  if (!profile) return PRESETS.find(p => p.id === 'forest');
  if (profile.themeMode !== 'own') return wpColours(wp || {});
  if (profile.theme === 'device') return PRESETS.find(p => p.id === (systemDark() ? 'dark' : 'light'));
  if (profile.theme === 'custom' && profile.custom) return { id: 'custom', name: 'Your own colours', ...profile.custom };
  return PRESETS.find(p => p.id === profile.theme) || PRESETS[0];
}

export function applyTheme(colours) {
  const d = derive(colours);
  const root = document.documentElement;
  for (const [k, v] of Object.entries(d.vars)) root.style.setProperty(k, v);
  root.dataset.light = d.light ? '1' : '0';
  root.style.colorScheme = d.light ? 'light' : 'dark';
  document.querySelector('meta[name="theme-color"]')?.setAttribute('content', colours.panel);
  try { localStorage.setItem('dp:theme', JSON.stringify({ vars: d.vars, light: d.light, panel: colours.panel })); } catch { /* private mode */ }
}
